import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import { VersionMismatchBadge } from '@thedealersconcierge/components/src/badge/versionMismatch';
import useOfflineNotification from '@thedealersconcierge/components/src/hooks/useOfflineNotification';
import { platformVersion } from '@thedealersconcierge/lib/platform-version';
import { useAtomValue } from 'jotai';
import posthog from 'posthog-js';
import { Fragment, Suspense, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CustomErrorBoundary from '~/components/CustomErrorBoundary';
import KioskModeHook from '~/components/hooks/KioskModeHook';
import config from '~/config';
import { g } from '~/globals';
import { platformVersionQuery } from '~/lib/cache/keys/platformVersion';
import meQuery from '~/queries/meQuery';
import { kioskDealershipAtom } from '~/state/kiosk';

const LoadAtoms = () => {
  useAtomValue(kioskDealershipAtom);

  return null;
};

/**
 * This hook Chagnes the fav icon in accordance with the environment we are
 * using.
 *
 * @returns
 */
const FavIconHook = () => {
  useEffect(() => {
    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }

    if (config.prodEnv) {
      link.href = `/fav/logo-${config.prodEnv}.png`;
    } else if (!config.isProduction) {
      link.href = `/fav/logo-local.png`;
    }
  }, []);

  return null;
};

const usePostHog = () => {
  const location = useLocation();

  const isLoggedIn = useAtomValue(g.reg.auth.isLoggedIn());

  const { data: meData } = useQuery({ ...meQuery(), enabled: isLoggedIn });

  // Identify
  useEffect(() => {
    if (posthog && meData?.me?.user?.id) {
      posthog.identify(
        // Replace 'distinct_id' with your user's unique identifier

        meData?.me?.user?.id,

        // There is currently no need to over share with post hoq
        {}
      );
    }
  }, [meData?.me?.user?.id]);

  // Track pageviews
  useEffect(() => {
    if (posthog) {
      posthog.capture('$pageview', {
        $current_url: window.location.href
      });
    }
  }, [location]);
};

const App = () => {
  const handleOnError = (error: Error) => {
    Sentry.captureException(error);
    toast.error(`Error: ${error.message}`);
  };

  usePostHog();

  useOfflineNotification(config.rawBackendUrl);

  // The backend's platform version is pushed to us on the realtime SSE heartbeat
  // and cached by the server-events listener — we no longer poll for it. This
  // query never fetches (see platformVersionQuery); it only subscribes to that
  // cached value so a new deploy still surfaces the refresh prompt.
  const { data: backendVersion } = useQuery(platformVersionQuery());

  // Triggered when frontend is running an outdated version compared to backend
  if (typeof backendVersion === 'string' && backendVersion !== platformVersion) {
    toast(<VersionMismatchBadge />, {
      autoClose: false,
      closeButton: false,
      toastId: 'version-mismatch-badge',
      closeOnClick: false,
      style: { maxWidth: '380px' }
    });
  }

  return (
    <Fragment>
      {/* Toast container somehow need to be initiated before the error boundary else it will not show to popup */}
      <ToastContainer position="bottom-right" />
      <CustomErrorBoundary
        onError={(error) => {
          handleOnError(error);
        }}
      >
        <Suspense>
          {/* Load atoms first */}
          <LoadAtoms />

          <FavIconHook />
          <KioskModeHook />

          <Outlet />
        </Suspense>
      </CustomErrorBoundary>
    </Fragment>
  );
};

export default App;
